import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { TenantService } from 'src/app/core/services/tenant.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { IdentityServiceService } from '../admin/system-configuration/shared/services/identity-service.service';
import { SystemConfigurationService } from '../admin/system-configuration/shared/services/system-configuration.service';
import { UserService } from '../admin/user/shared/services/user.service';
import { MasterLookupService } from '../master/master-services/master-lookup.service';
import { GlobalDataService } from '../shared/services/global-data.service';
import { MessageHubService } from '../shared/services/message-hub.service';
import { LobbyService } from './../lobby/shared/services/lobby.service';
import * as CoreActions from './actions/core.actions';

@Injectable()
export class CoreEffects {
    constructor(private actions$: Actions,
        private toastService: ToastService,
        private userService: UserService,
        private tenantService: TenantService,
        private identityServerService: IdentityServiceService,
        private messageHubService: MessageHubService,
        private lobbyService: LobbyService,
        private systemConfigurationService: SystemConfigurationService,
        private masterLookupService: MasterLookupService,
        public gloabDataService: GlobalDataService) { }

    initializeVirtualConferencingProvider$ = createEffect(() => this.actions$.pipe(
        ofType(CoreActions.initializeVirtualConferencingProvider),
        tap(() => {
            this.masterLookupService.getCurrentConferencingProvider().subscribe(currentConfProvider => {
                this.userService.getConferencingProvider().subscribe(latestConfProvider => {
                    console.log(`Current Provider: ${currentConfProvider}\nLatest Provider: ${latestConfProvider}`);
                    if (latestConfProvider !=='' &&(currentConfProvider == null  || (currentConfProvider != null && latestConfProvider != currentConfProvider))) {
                        this.systemConfigurationService.setSystemConferencingProvider(latestConfProvider).subscribe(res => {
                            if (!res)
                                console.error("Failed to initialize conferencing provider");
                            else console.info("Initialized Conferencing Provider");
                        });
                    }
                });
            });
        }),
    ), {
        dispatch: false
    });

    loadUserInfo$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.loadUserInfo),
            mergeMap(action =>
                this.userService.getLoggedInUserInfo()
                    .pipe(
                        map(info => CoreActions.updateUserInfo({
                            payload: {
                                info
                            }
                        }))
                    )
            )
        ));

    loadUrlCode$ = createEffect(() => this.actions$.pipe(
        ofType(CoreActions.loadUrlCode),
        mergeMap(action => this.identityServerService.getSystemConfiguration()
            .pipe(
                map((response) => {
                    return CoreActions.updateUrlCode({
                        payload:
                        {
                            urlCode: response.urlCode
                        }
                    });
                })
            )
        )

    ));

    loadSigninDetails$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.loadSignInDetailsForUser),
            switchMap(action => {
                this.messageHubService.subscribeToUserStatusGroup({
                    TenantId: action.payload.tenantId,
                    UserId: action.payload.userId,
                    LocationId: action.payload.locationId
                });
                return of(CoreActions.loadSignInDetailsSuccess());
            })
        ));

    loadLobbyLocationSelectorStatus$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.loadLobbyLocationSelectorStatus),
            switchMap(action => {
                this.messageHubService.subscribeToLocationSelectorStatusGroup(
                    {
                        UserId: action.payload.userId,
                        LocationId: action.payload.locationId,
                        TenantId: action.payload.tenantId
                    });
                return of(CoreActions.loadLobbyLocationSelectorStatusSuccess());
            })
        ));

    signoutUser$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(CoreActions.userSignOut),
                tap(a => console.log(a)),
                concatMap(action => this.lobbyService.signOutUser(action.payload)
                    .pipe(
                        map(response => CoreActions.loadSignInDetailsForUser({
                            payload:
                            {
                                userId: action.payload.userId,
                                tenantId: action.payload.tenantId,
                                locationId: action.payload.locationId
                            }
                        })),
                        catchError((error) => {
                            console.log('Core effects signoutUser$ error');
                            this.toastService.showError(error.error);
                            return of(CoreActions.loadSignInDetailsForUser({
                                payload:
                                {
                                    userId: action.payload.userId,
                                    tenantId: action.payload.tenantId,
                                    locationId: action.payload.locationId
                                }
                            }));
                        })
                    )
                )
            );
    });

    appLogoutUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.userLogOut),
            tap(a => console.log(a)),
            concatMap(action => this.lobbyService.signOutUser(action.payload)
                .pipe(
                    map(response => {
                        this.gloabDataService.logout();
                        return CoreActions.loadSignInDetailsForUser({
                            payload:
                            {
                                userId: action.payload.userId,
                                tenantId: action.payload.tenantId,
                                locationId: action.payload.locationId
                            }
                        });
                    }),
                    catchError((error) => {
                        this.toastService.showError(error.error);
                        return of(CoreActions.loadSignInDetailsForUser({
                            payload:
                            {
                                userId: action.payload.userId,
                                tenantId: action.payload.tenantId,
                                locationId: action.payload.locationId
                            }
                        }));
                    })
                )
            )
        ));

    timeOutUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.userTimeOut),
            tap(a => console.log(a)),
            concatMap(action => this.lobbyService.signOutUser(action.payload)
                .pipe(
                    map(response => {
                        this.gloabDataService.logout();
                        return CoreActions.loadSignInDetailsForUser({
                            payload:
                            {
                                userId: action.payload.userId,
                                tenantId: action.payload.tenantId,
                                locationId: action.payload.locationId
                            }
                        });
                    }),
                    catchError((error) => {
                        return of(CoreActions.loadSignInDetailsForUser({
                            payload:
                            {
                                userId: action.payload.userId,
                                tenantId: action.payload.tenantId,
                                locationId: action.payload.locationId
                            }
                        }));
                    })
                )
            )
        ));

    signinUser$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.userSignIn),
            tap(a => console.log(a)),
            mergeMap(action => this.lobbyService.signInUser(action.payload)
                .pipe(
                    map(response => CoreActions.loadSignInDetailsForUser({
                        payload:
                        {
                            userId: action.payload.userId,
                            tenantId: action.payload.tenantId,
                            locationId: action.payload.locationId
                        }
                    })),
                    catchError((error) => {
                        this.toastService.showError(error.error);
                        return of(CoreActions.loadSignInDetailsForUser({
                            payload:
                            {
                                userId: action.payload.userId,
                                tenantId: action.payload.tenantId,
                                locationId: action.payload.locationId
                            }
                        }));
                    })
                )
            )
        ));

    loadAvailablaeTenants$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.loadAvailableTenants),
            mergeMap(action => this.tenantService.fetchAvailableTenants()
                .pipe(
                    map(response => CoreActions.updateAvailableTenants({
                        payload:
                        {
                            tenants: response
                        }
                    }))
                )
            )
        ));

    loadSystemConfigurations$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.loadSystemConfigurations),
            switchMap(action => {
                this.messageHubService.subscribeToSystemConfigurationGroup(
                    {
                        LocationId: action.payload.locationId,
                        TenantId: action.payload.tenantId,
                        UserId: action.payload.userId
                    });
                return of(CoreActions.loadSystemConfigurationSuccess());
            })
        ));
    loadSystemConfigurationsApi$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.loadSystemConfigurationsApi),
            mergeMap(action => this.systemConfigurationService
                .getSystemConfigurationForSignInUser(action.payload.locationId, action.payload.userId)
                .pipe(
                    map(systemConfigurations => {
                        return CoreActions.loadSystemConfigurationApiSuccess({ payload: { systemConfigurations } });
                    }),
                    catchError(error => {
                        console.log('Error fetching the configurations');
                        return of(CoreActions.loadSystemConfigurationApiFailure());
                    })
                )
            )
        ));

    loadExternalClientConfigurations$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.loadACClientConfiguration),
            mergeMap(() => this.masterLookupService.getACClientConfiguration()
                .pipe(
                    map(response => CoreActions.updateACClientConfiguration({
                        payload: {
                            acClientConfiguration: response
                        }
                    }))
                )
            )
        ));

    loadTenantLogo$ = createEffect(() => this.actions$.pipe(
        ofType(CoreActions.loadTenantLogo),
        mergeMap(() =>
            this.tenantService.getLogoUrl(false).pipe(
                map(url => CoreActions.updateTenantLogo(
                    {
                        payload: {
                            tenantLogoUrl: url
                        }
                    }
                ))
            ))
    ));

    subscribeToSyncStatus$ = createEffect(() => this.actions$.pipe(
        ofType(CoreActions.subscribeToSyncStatus),
        switchMap(action => {
            this.messageHubService.subscribeToSyncGroup({
                TenantId: action.payload.TenantId,
                UserId: action.payload.UserId
            });
            return of(CoreActions.subscribeToSyncStatusSuccess());
        })
    ));

    unSubscribeFromSignalRGroups$ = createEffect(() => this.actions$
        .pipe(
            ofType(CoreActions.unsubscribeFromSignalRGroups),
            switchMap(action => {
                this.messageHubService.unSubscribeFromGroups(action.groupNames);
                return of(CoreActions.unsubscribeFromSignalRGroupsSuccess());
            })
        ));

    subscribeToCreateClientStatus$ = createEffect(() => this.actions$.pipe(
        ofType(CoreActions.subscribeToCreateClientStatus),
        switchMap(action => {
            this.messageHubService.subscribeToClientCreateGroup({
                CreatedExternalUserId: action.payload.CreatedExternalUserId
            });
            return of(CoreActions.subscribeToCreateClientSuccess());
        })
    ));
}
